<template>
  <div class="sales-dashboard" :class="{ 'sales-dashboard--dark': isDarkSkin }">

    <div class="sales-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"
                   v-bind:hidden-fields="{'buTypes': viewKey!=='sales-dashboard' }"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <b-row class="kpi-row">
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon">
              <feather-icon icon="UsersIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD / Total Clients') }}</div>
              <div class="kpi-card__value">{{ statistics.ftdsCount }} <span class="kpi-card__value-sep">/</span> {{ statistics.clientsCount }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="WifiIcon" size="12" class="mr-25"/>
                {{ $t('Online') }}: {{ statistics.onlineClients }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon">
              <feather-icon icon="DollarSignIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalDeposits) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="TrendingUpIcon" size="12" class="mr-25"/>
                {{ $t('Net') }}: {{ convertToEURO(statistics.netDeposits) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--warning">
            <div class="kpi-card__icon">
              <feather-icon icon="ActivityIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Closed P&L') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(closedPL) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="BarChart2Icon" size="12" class="mr-25"/>
                {{ $t('Open P&L') }}: {{ convertToEURO(openPL) }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--danger">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="PercentIcon" size="12" class="mr-25"/>
                {{ $t('Rate') }}: {{ withdrawalRate }}%
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <!-- Sales performance strip -->
      <b-row class="perf-row">
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--blue"><feather-icon icon="TargetIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ conversionRate }}%</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.conversionRate') }}</div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--green"><feather-icon icon="DollarSignIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ convertToEURO(avgDeposit) }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.avgDeposit') }}</div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--purple"><feather-icon icon="UsersIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ activeAgents }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.activeAgents') }}</div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--amber"><feather-icon icon="HashIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ depositTxCount }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.depositsCount') }}</div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Sales agents + Retention agents leaderboards -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="PhoneCallIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('salesDashboard.salesAgents') }}</h5>
              </div>
              <span class="panel-card__hint sales-total">
                {{ $t('salesDashboard.newLeads') }}: <strong>{{ salesTotals.leads }}</strong>
                <span class="mx-50">·</span>
                {{ $t('salesDashboard.ftd') }}: <strong>{{ salesTotals.ftd }}</strong>
              </span>
            </div>
            <div class="panel-card__body">
              <top-agents-leaderboard :agents="statistics.salesAgentBreakdown" metric="ftd"
                                      :loading="loading" :is-dark-skin="isDarkSkin"
                                      @select="openSalesman($event)"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="AwardIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('salesDashboard.retentionAgents') }}</h5>
              </div>
              <span class="panel-card__hint"><feather-icon icon="MousePointerIcon" size="12"/> {{ $t('salesDashboard.clickToDrill') }}</span>
            </div>
            <div class="panel-card__body">
              <top-agents-leaderboard :agents="statistics.agentBreakdown" :loading="loading"
                                      :is-dark-skin="isDarkSkin" @select="openDrill('AGENT', $event)"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Deposits by Country + Clients by Status -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="GlobeIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Deposits by Country') }}</h5>
              </div>
              <div class="panel-card__toggle">
                <span :class="{'text-muted': showCountryDeposit}">€</span>
                <b-form-checkbox switch v-model="showCountryDeposit" class="mx-50"/>
                <span :class="{'text-muted': !showCountryDeposit}">#</span>
              </div>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="depositCountryItems" :currency="!showCountryDeposit"
                               :loading="loading" :is-dark-skin="isDarkSkin"
                               @select="openDrill('DEPOSIT_COUNTRY', $event)"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt"><feather-icon icon="PieChartIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Status') }}</h5>
              </div>
              <span class="panel-card__hint"><feather-icon icon="MousePointerIcon" size="12"/> {{ $t('salesDashboard.clickToDrill') }}</span>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="statusItems" :loading="loading"
                               :is-dark-skin="isDarkSkin" @select="openDrill('CLIENT_STATUS', $event)"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Clients by Country -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt"><feather-icon icon="MapPinIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Country') }}</h5>
              </div>
              <span class="panel-card__hint"><feather-icon icon="MousePointerIcon" size="12"/> {{ $t('salesDashboard.clickToDrill') }}</span>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="clientCountryItems" :loading="loading"
                               :is-dark-skin="isDarkSkin" @select="openDrill('CLIENT_COUNTRY', $event)"/>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>

    <sales-drill-modal ref="drill"/>
    <salesman-modal ref="salesman"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import DashboardSearchRequest from '@/model/Dashboard/DashboardSearchRequest';
import FilterCard from '@/components/widget/FilterCard';
import SalesDashboardFilterFields from '@/model/filter/SalesDashboardFilterFields';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import TopAgentsLeaderboard from '@/components/dashboard/TopAgentsLeaderboard.vue';
import RankedBarList from '@/components/dashboard/RankedBarList.vue';
import SalesDrillModal from '@/components/modal/SalesDrillModal.vue';
import SalesmanModal from '@/components/modal/SalesmanModal.vue';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'SalesDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    TopAgentsLeaderboard,
    RankedBarList,
    SalesDrillModal,
    SalesmanModal,
    CalendarBar,
    FilterCard,
  },

  data: () => ({
    filter: DashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: '', until: '' },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    statistics: {
      totalDeposits: 0,
      netDeposits: 0,
      totalWithdrawals: 0,
      onlineClients: 0,
      clientsCount: 0,
      ftdsCount: 0,
      userDeposit: {},
      userCount: {},
      agentBreakdown: [],
      salesAgentBreakdown: [],
      countryDeposit: {},
      countryCount: {},
      statusMapCount: {},
      clientCountryMapCount: {},
    },
    closedPL: 0,
    openPL: 0,
    showCountryDeposit: false,
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('clients', ['allStatuses']),
    viewKey() {
      return this.$route.name || 'sales-dashboard';
    },
    countryDepositData() {
      return !this.showCountryDeposit ? this.statistics.countryDeposit : this.statistics.countryCount;
    },
    statusMap() {
      return (this.allStatuses || []).reduce((m, s) => {
        m[s.id] = { name: s.status, color: s.color };
        return m;
      }, {});
    },
    countryMap() {
      const list = this.$t('countryList') || [];
      return (Array.isArray(list) ? list : []).reduce((m, c) => {
        m[c.code] = c.name;
        return m;
      }, {});
    },
    statusItems() {
      const map = this.statistics.statusMapCount || {};
      return Object.keys(map).map(id => {
        const meta = this.statusMap[id] || { name: id, color: '#999999' };
        return { key: id, label: meta.name, color: meta.color, value: map[id] };
      }).sort((a, b) => b.value - a.value);
    },
    clientCountryItems() {
      const map = this.statistics.clientCountryMapCount || {};
      return Object.keys(map).map(code => ({
        key: code,
        label: this.countryMap[code] || code || 'Unknown',
        flag: this.flag(code),
        value: map[code],
      })).sort((a, b) => b.value - a.value);
    },
    depositCountryItems() {
      const map = this.countryDepositData || {};
      return Object.keys(map).map(code => ({
        key: code,
        label: this.countryMap[code] || code || 'Unknown',
        flag: this.flag(code),
        value: Number(map[code]),
      })).sort((a, b) => b.value - a.value);
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    },
    depositTxCount() {
      return (this.statistics.agentBreakdown || []).reduce((s, a) => s + (a.count || 0), 0);
    },
    avgDeposit() {
      const c = this.depositTxCount;
      return c ? (this.statistics.totalDeposits || 0) / c : 0;
    },
    conversionRate() {
      const clients = this.statistics.clientsCount || 0;
      if (!clients) return '0.0';
      return ((this.statistics.ftdsCount || 0) / clients * 100).toFixed(1);
    },
    activeAgents() {
      return (this.statistics.agentBreakdown || []).filter(a => (a.deposit || 0) > 0).length;
    },
    // Totals for the Sales table: new leads (created in range) handled by sales
    // agents, and how many converted to FTD.
    salesTotals() {
      const list = this.statistics.salesAgentBreakdown || [];
      const leads = list.reduce((t, a) => t + (a.salesLeads || 0), 0);
      const ftd = list.reduce((t, a) => t + (a.salesFtd || 0), 0);
      return { leads, ftd, conv: leads ? (ftd / leads * 100).toFixed(0) : '0' };
    },
    drillCtx() {
      return {
        orgIds: this.filter.orgIds,
        buIds: this.filter.buIds,
        buTypes: this.filter.buTypes,
        affiliateId: this.filter.affiliateId,
        banner: this.filter.banner,
        campaign: this.filter.campaign,
        realOnly: this.filter.realOnly,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
    },
  },
  created() {
    this.filterFields = SalesDashboardFilterFields();
    // Ensure client statuses (names + colors) are available for the status list.
    if (!this.allStatuses || !this.allStatuses.length) {
      this.refreshData();
    }
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getSalesDashboard']),
    ...mapActions('clients', ['refreshData']),
    flag(code) {
      if (!code || code.length !== 2) return '';
      return code.toUpperCase().replace(/./g, ch => String.fromCodePoint(127397 + ch.charCodeAt(0)));
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getSalesDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getSalesDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    openSalesman(payload) {
      if (!payload || !payload.value) return;
      this.$refs.salesman.open({ userId: payload.value, label: payload.label, ctx: this.drillCtx });
    },
    openDrill(dimension, payload) {
      if (!payload || payload.value == null || payload.value === '') return;
      this.$refs.drill.open({
        dimension,
        value: payload.value,
        label: payload.label,
        ctx: this.drillCtx,
      });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.onlineClients = data.clientsOnline || 0;
      this.statistics.ftdsCount = data.ftdsCount || 0;
      this.statistics.clientsCount = data.clientsCount || 0;
      this.statistics.userDeposit = data.userDeposit || {};
      this.statistics.userCount = data.userCount || {};
      this.statistics.agentBreakdown = data.agentBreakdown || [];
      this.statistics.salesAgentBreakdown = data.salesAgentBreakdown || [];
      this.statistics.countryDeposit = data.countryDeposit || {};
      this.statistics.countryCount = data.countryCount || {};
      this.statistics.statusMapCount = data.statusMapCount || {};
      this.statistics.clientCountryMapCount = data.clientCountryMapCount || {};
      this.closedPL = data.closedPL || 0;
    },
  }
};
</script>

<style scoped lang="scss">
.sales-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-row {
  margin-top: 0.5rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-sep {
    opacity: 0.55;
    font-weight: 500;
    margin: 0 0.15rem;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22),
              0 1px 3px rgba(20, 24, 50, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px -18px rgba(20, 24, 50, 0.28),
                0 2px 4px rgba(20, 24, 50, 0.05);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  &__icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04));
    color: #007BFF;

    &--alt {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.04));
      color: #10b981;
    }
  }

  &__title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__toggle {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.95rem;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    flex: 1;
  }
}

.perf-row {
  margin-top: 0.25rem;
}

.perf-chip {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1.1rem;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 20px -14px rgba(20, 24, 50, 0.2), 0 1px 2px rgba(20, 24, 50, 0.04);
  height: 100%;

  &__icon {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    color: #fff;

    &--blue { background: linear-gradient(135deg, #4da3ff, #007BFF); }
    &--green { background: linear-gradient(135deg, #34d399, #10b981); }
    &--purple { background: linear-gradient(135deg, #a78bfa, #7c3aed); }
    &--amber { background: linear-gradient(135deg, #fbbf24, #f59e0b); }
  }
  &__value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2a44;
    line-height: 1.1;
  }
  &__label {
    font-size: 0.74rem;
    color: #6e6b7b;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-top: 0.15rem;
  }
}

.panel-card__hint {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  color: #b9b9c3;
  font-style: italic;
}
.panel-card__hint.sales-total {
  font-style: normal;
  font-size: 0.78rem;
  color: #6e6b7b;
  white-space: nowrap;
  strong { color: #1f2a44; font-weight: 700; }
  .sales-total__conv { color: #10b981; font-weight: 700; margin-left: 0.2rem; }
}

.sales-dashboard--dark {
  .panel-card {
    background: #283046;
    box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6);
  }

  .perf-chip {
    background: #283046;
    box-shadow: 0 8px 20px -14px rgba(0, 0, 0, 0.55);
    .perf-chip__value { color: #d0d2d6; }
    .perf-chip__label { color: #8b93ab; }
  }

  .panel-card__hint { color: #8b93ab; }
  .panel-card__hint.sales-total { color: #8b93ab; strong { color: #d0d2d6; } }

  .panel-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .panel-card__title {
    color: #d0d2d6;
  }

  .panel-card__icon-chip {
    background: linear-gradient(135deg, rgba(77, 163, 255, 0.25), rgba(77, 163, 255, 0.05));
    color: #a8b6ff;

    &--alt {
      background: linear-gradient(135deg, rgba(46, 213, 162, 0.25), rgba(46, 213, 162, 0.05));
      color: #6bdcb1;
    }
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
